import 'src/styles/tailwind.css'

import { lazy, memo, Suspense, useMemo } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import AdomikLoader from 'src/components/ui/atoms/PlaceHolder/AdomikLoader'
import PrivateRouteGuard from 'src/components/ui/atoms/Router/PrivateRouteGuard'
import Layout from 'src/components/ui/molecules/Layout'
import NotFound from 'src/components/ui/organisms/NotFound'
import commonRoutes from 'src/routes/commonRoutes'
import productsRoutes from 'src/routes/productsRoutes'
import publicRoutes, { COGNITO_CALLBACK_PATH, KAGEMUSHA_PATH } from 'src/routes/publicRoutes'
import useAccount from './hooks/useAccount'
import useHasMappingRightAccess from './hooks/useHasMappingRightAccess'
import selfMappingServiceRoutes from './routes/selfMappingServiceRoutes'
import useMenusStore from './stores/useMenusStore'
import AppRouteInterface from './types/AppRouteInterface'
import { isTrueOrUndefinedFn } from './utils/booleanUtils'
import { isOnAppRoutesFn } from './utils/routeUtils'

// v6 routes match the entire URL by default. To preserve v5's
// non-exact prefix-match for routes that mount nested <Routes>
// (e.g. SSMPage, AccountPage), append a trailing splat.
const withSplat = (path: string) => path.replace(/\/?$/, '/*')

const LOGGED_ROUTES = [...productsRoutes, ...commonRoutes]
const RefinerLoader = lazy(() => import('./components/ui/molecules/RefinerLoader'))
const PendoLoader = lazy(() => import('./components/ui/molecules/PendoLoader'))

const AuthenticatedRouter = () => {
  const { pathname } = useLocation()
  const menus = useMenusStore(s => s.menus)
  const { informations: account, isLogged } = useAccount()
  const hasMappingAccess = useHasMappingRightAccess()

  // Has right access to self service mapping page
  const selfMappingRoutes: AppRouteInterface[] = hasMappingAccess ? selfMappingServiceRoutes : []

  const allowedRoutes = useMemo(() => {
    return [
      ...productsRoutes.filter(route => {
        const productFromPath = route.path.split('/')[1]
        const subProductFromPath = route.path.split('/')[2]
        const programFromPath = route.path.split('/')[3]

        if (productFromPath && subProductFromPath?.startsWith(':')) {
          return menus.some(category => {
            const categoryProduct = category.children?.find(
              p => p.code === productFromPath && isTrueOrUndefinedFn(p.has_access),
            )
            if (!categoryProduct) {
              return false
            }

            if (programFromPath) {
              return categoryProduct.children?.some(sbp =>
                programFromPath.startsWith(':')
                  ? Boolean(sbp.program_list?.length)
                  : sbp.program_list?.some(d => d.id?.toString() === programFromPath.toString()),
              )
            }
            return Boolean(categoryProduct.children)
          })
        }

        if (productFromPath && subProductFromPath) {
          return menus.some(category => {
            const categoryProduct = category.children?.find(
              p => p.code === productFromPath && isTrueOrUndefinedFn(p.has_access),
            )
            if (!categoryProduct) {
              return false
            }
            return categoryProduct.children?.some(
              sbp => sbp.code === subProductFromPath && isTrueOrUndefinedFn(sbp.has_access),
            )
          })
        }

        return false
      }),
      ...commonRoutes,
      ...selfMappingRoutes,
    ]
  }, [menus])

  // Check if the route typed in the url exist

  const currentPath =
    pathname.charAt(pathname.length - 1) === '/' ? pathname.slice(0, -1) : pathname

  // Check if the user has access to the route
  const isRouteNotAllowed =
    isOnAppRoutesFn(LOGGED_ROUTES, currentPath) && !isOnAppRoutesFn(allowedRoutes, currentPath)

  const isPublicRoute = publicRoutes.some(route => route.path === currentPath)
  let defaultRoute = allowedRoutes[0]?.path

  // If user tried to access a public route (login, confirmation password or forgotten password)
  // redirect him to logged route
  if (isPublicRoute) return <Navigate to={defaultRoute || '/account/user'} replace />
  // If route is not allowed or user has no profile, redirect him to account page
  if (!account.current_profile || isRouteNotAllowed) return <Navigate to="/account/user" replace />

  if (defaultRoute) {
    const subProductPath = defaultRoute.split('/')[2]
    const programPath = defaultRoute.split('/')[3]
    if (subProductPath?.startsWith(':')) {
      const productCode = defaultRoute.split('/')[1]
      const product = menus
        .find(category =>
          category.children?.some(p => p.code === productCode && isTrueOrUndefinedFn(p.has_access)),
        )
        ?.children?.find(p => p.code === productCode && isTrueOrUndefinedFn(p.has_access))
      if (product && product.children?.length > 0) {
        const firstChild = product.children[0]
        if (firstChild) {
          let program: number | undefined

          if (programPath) {
            program = programPath?.startsWith(':')
              ? firstChild?.program_list?.[0]?.id
              : firstChild?.program_list?.find(d => d.id?.toString() === programPath.toString())?.id
          }

          const { code } = firstChild
          const programParam = program ? `/${program}` : ''
          defaultRoute = `/${productCode}/${code}${programParam}`
        }
      }
    }
  }

  return (
    <Layout>
      <Routes>
        {allowedRoutes.map(({ path, component: Component }) => (
          <Route key={path} path={withSplat(path)} element={<Component />} />
        ))}
        <Route path="/" element={<Navigate to={defaultRoute} replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Suspense fallback={null}>{isLogged && <RefinerLoader />}</Suspense>
      <Suspense fallback={null}>{isLogged && <PendoLoader />}</Suspense>
    </Layout>
  )
}

const AppRouteInterfacer = memo(() => {
  const { isLogged } = useAccount()

  return (
    <Suspense fallback={<AdomikLoader />}>
      <Routes>
        {/* Publics routes declaration. The Cognito callback and the impersonation
            link are declared even when a token is stored: they replace that
            token, possibly expired, with the fresh one. */}
        {publicRoutes
          .filter(
            ({ path }) => !isLogged || path === COGNITO_CALLBACK_PATH || path === KAGEMUSHA_PATH,
          )
          .map(({ path, component: Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}

        <Route
          path="/*"
          element={
            <PrivateRouteGuard>
              <AuthenticatedRouter />
            </PrivateRouteGuard>
          }
        />
      </Routes>
    </Suspense>
  )
})

export default AppRouteInterfacer
